import * as React from "react"
import { AlertTriangle, ArrowRight, Check, ExternalLink, FileText, Loader2, ShieldCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useBookingT } from "@/hooks/use-translations"
import { toast } from "sonner"
import {
  acceptDocument,
  fetchActivationPackage,
  grantConsent,
  ActivationPackage,
  ActivationStep,
} from "./api"
import { sampleActivationPackage } from "./sample"

/** Development-only fallback so the flow can be reviewed before the API ships. */
const isDev = process.env.NODE_ENV === "development"

/**
 * Turning a product on, one screen at a time.
 *
 * The one-step-at-a-time shape is not a style choice. Schedule C section 5.1
 * requires the card data-sharing consent to be given by a separate affirmative
 * action on its own screen, never bundled with acceptance of the Master, the
 * Schedule, or anything else — a requirement flowed down from the Pliant
 * Partnership Agreement section 24.11 and named in spec 13 section 1.3 as the
 * single most likely thing to be got wrong. Rendering the steps as a list with
 * one submit would break it, so this component can only ever show one.
 *
 * The same reasoning covers the card partner's and the issuing bank's
 * agreements: those are contracts between the customer and them. Each gets its
 * own screen that says whose agreement it is, and its own acceptance record.
 */

function StepProgress({ steps, index }: { steps: ActivationStep[]; index: number }) {
  const t = useBookingT()

  return (
    <div className="space-y-1.5">
      <p className="text-xs uppercase tracking-wide text-neutral-700">
        {t("legal_step", "Step")} {index + 1} {t("legal_step_of", "of")} {steps.length}
      </p>
      <div className="flex gap-1">
        {steps.map((step, i) => (
          <span
            key={step.code}
            className={
              "h-1 flex-1 rounded-full " +
              (step.satisfied || i < index ? "bg-brand-500" : i === index ? "bg-brand-300" : "bg-neutral-200")
            }
          />
        ))}
      </div>
    </div>
  )
}

/** Our own contract: read it here, agree to it, affirm authority to bind. */
function ClickwrapStep({
  step,
  simulate,
  onRead,
  onDone,
}: {
  step: ActivationStep
  simulate: boolean
  onRead: (target: { code: string; name: string; versionId?: number }) => void
  onDone: () => void
}) {
  const t = useBookingT()
  const [agreed, setAgreed] = React.useState(false)
  const [authority, setAuthority] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const affirmation = t("legal_affirm_prefix", "I have read and agree to:") + " " + step.name

  const submit = async () => {
    if (!agreed || !authority || saving || step.versionId === null) return
    if (simulate) {
      onDone()
      return
    }
    setSaving(true)
    try {
      await acceptDocument({
        versionId: step.versionId,
        affirmationText: affirmation,
        authorityAffirmed: authority,
      })
      onDone()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      {step.summary && <p className="text-sm text-neutral-800">{step.summary}</p>}

      <button
        type="button"
        onClick={() => onRead({ code: step.code, name: step.name, versionId: step.versionId ?? undefined })}
        className="inline-flex items-center gap-1.5 text-sm text-brand-600 underline underline-offset-2"
      >
        <FileText className="h-4 w-4" />
        {t("legal_read_full", "Read the full document")}
      </button>

      <label className="flex items-start gap-2.5 cursor-pointer">
        <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5" />
        <span className="text-sm text-neutral-900">{affirmation}</span>
      </label>

      <label className="flex items-start gap-2.5 cursor-pointer">
        <Checkbox checked={authority} onCheckedChange={(v) => setAuthority(v === true)} className="mt-0.5" />
        <span className="text-sm text-neutral-900">
          {t("legal_affirm_authority", "I am authorised to enter into this agreement on behalf of my company.")}
        </span>
      </label>

      <DialogFooter>
        <Button onClick={submit} disabled={!agreed || !authority || saving}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {t("legal_accept_continue", "Accept and continue")}
          <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </DialogFooter>
    </div>
  )
}

/** Somebody else's contract. The screen has to say so before it is agreed to. */
function ThirdPartyStep({
  step,
  simulate,
  onDone,
}: {
  step: ActivationStep
  simulate: boolean
  onDone: () => void
}) {
  const t = useBookingT()
  const [agreed, setAgreed] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const party = step.counterparty || t("legal_third_party_generic", "the provider")

  // Authority is folded into the sentence rather than asked again in a second
  // box. It still has to be affirmed — the customer is binding their company to
  // somebody else's contract — but a fifth identical checkbox in a row is how
  // people learn to tick without reading, and the stored boolean has to match
  // words the person actually saw.
  const affirmation =
    t("legal_affirm_prefix", "I have read and agree to:") +
    " " +
    step.name +
    " (" +
    party +
    "). " +
    t("legal_affirm_authority_inline", "I am authorised to enter into it on behalf of my company.")

  const submit = async () => {
    if (!agreed || saving || step.versionId === null) return
    if (simulate) {
      onDone()
      return
    }
    setSaving(true)
    try {
      await acceptDocument({
        versionId: step.versionId,
        affirmationText: affirmation,
        authorityAffirmed: true,
      })
      onDone()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Named plainly and up front. Burying a contract with another company
          inside a Travel Code checkbox is the thing spec 13 section 1.3 rules
          out, and a customer who does not know who they are contracting with
          cannot have agreed to it. */}
      <div className="rounded-lg border border-warning-300 bg-warning-100 px-3 py-2.5 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-warning-700 shrink-0 mt-0.5" />
        <p className="text-sm text-neutral-900">
          {t("legal_third_party_notice_a", "This is an agreement between your company and")}{" "}
          <span className="font-semibold">{party}</span>
          {t("legal_third_party_notice_b", " — not with Travel Code. Travel Code is not a party to it.")}
        </p>
      </div>

      {step.summary && <p className="text-sm text-neutral-800">{step.summary}</p>}

      {step.externalUrl && (
        <a
          href={step.externalUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-brand-600 underline underline-offset-2"
        >
          <ExternalLink className="h-4 w-4" />
          {t("legal_read_at_party", "Read the full text on")} {party}
        </a>
      )}

      <label className="flex items-start gap-2.5 cursor-pointer">
        <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5" />
        <span className="text-sm text-neutral-900">{affirmation}</span>
      </label>

      <DialogFooter>
        <Button onClick={submit} disabled={!agreed || saving}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {t("legal_accept_continue", "Accept and continue")}
          <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </DialogFooter>
    </div>
  )
}

/**
 * The consent screen. Nothing else is on it, the box starts unchecked, and what
 * withdrawing does later is stated before it is given rather than after.
 */
function ConsentStep({
  step,
  screenVersion,
  simulate,
  onDone,
}: {
  step: ActivationStep
  screenVersion: string
  simulate: boolean
  onDone: () => void
}) {
  const t = useBookingT()
  const [agreed, setAgreed] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const submit = async () => {
    if (!agreed || saving) return
    if (simulate) {
      onDone()
      return
    }
    setSaving(true)
    try {
      await grantConsent(step.code, screenVersion)
      onDone()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-brand-200 bg-brand-100 px-3 py-2.5 flex items-start gap-2">
        <ShieldCheck className="h-4 w-4 text-brand-600 shrink-0 mt-0.5" />
        <p className="text-sm text-neutral-900">
          {t(
            "legal_consent_separate",
            "This permission is asked for on its own. It is not part of any agreement you accepted, and you can withdraw it later without ending the rest."
          )}
        </p>
      </div>

      {step.summary && <p className="text-sm text-neutral-900">{step.summary}</p>}

      {/* Not a hint — it is the consequence of the thing being agreed to, so it
          is set at the same weight as the rest of the screen. */}
      {step.withdrawal && <p className="text-sm text-neutral-800">{step.withdrawal}</p>}

      {step.affirmation && (
        <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-neutral-200 p-3">
          <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5" />
          <span className="text-sm font-medium text-neutral-900">{step.affirmation}</span>
        </label>
      )}

      <DialogFooter>
        <Button onClick={submit} disabled={!agreed || saving}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {t("legal_give_consent", "Give consent")}
        </Button>
      </DialogFooter>
    </div>
  )
}

export function ProductActivationDialog({
  productCode,
  productName,
  open,
  onOpenChange,
  onComplete,
  onRead,
}: {
  productCode: string
  productName: string
  open: boolean
  onOpenChange: (v: boolean) => void
  onComplete?: () => void
  onRead: (target: { code: string; name: string; versionId?: number }) => void
}) {
  const t = useBookingT()
  const [pkg, setPkg] = React.useState<ActivationPackage | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [usingSample, setUsingSample] = React.useState(false)

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      setPkg(await fetchActivationPackage(productCode))
      setUsingSample(false)
      setError(null)
    } catch (e) {
      if (isDev && productCode === "CARD") {
        setPkg(sampleActivationPackage())
        setUsingSample(true)
        setError(null)
      } else {
        setError((e as Error).message)
      }
    } finally {
      setLoading(false)
    }
  }, [productCode])

  React.useEffect(() => {
    if (open) void load()
  }, [open, load])

  // After a step is done, ask the server again rather than assuming: it is what
  // decides whether the product may turn on, and re-reading also picks up
  // anything that changed underneath. In sample mode there is nothing to ask,
  // so the step is marked done locally.
  const advance = React.useCallback(() => {
    if (!usingSample) {
      void load()
      return
    }

    setPkg((previous) => {
      if (!previous) return previous
      const next = previous.steps.findIndex((s) => !s.satisfied)
      if (next < 0) return previous
      const steps = previous.steps.map((s, i) => (i === next ? { ...s, satisfied: true } : s))
      return { ...previous, steps, complete: steps.every((s) => s.satisfied) }
    })
  }, [usingSample, load])

  const steps = pkg?.steps || []
  // The next thing to do is always the first unfinished step. Deriving it from
  // the server's answer rather than tracking a cursor means a reload, a second
  // tab, or a half-finished attempt from yesterday all resume in the right
  // place instead of re-asking for something already given.
  const index = steps.findIndex((s) => !s.satisfied)
  const current = index >= 0 ? steps[index] : null

  React.useEffect(() => {
    if (pkg?.complete) onComplete?.()
  }, [pkg?.complete, onComplete])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">
            {current ? current.name : t("legal_activation_done_title", "You are all set")}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-warning-300 bg-warning-100 px-3 py-2 text-sm text-warning-800">
            {error}
          </div>
        )}

        {!loading && usingSample && (
          <div className="rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800">
            {t("legal_sample_notice", "Showing sample data — the documents API is not available in this environment.")}
          </div>
        )}

        {!loading && pkg && !pkg.maySign && (
          <p className="text-sm text-neutral-700">
            {t(
              "legal_owner_only",
              "Accepting agreements binds the company, so only the account owner can do it. Ask them to review the documents below."
            )}
          </p>
        )}

        {/* A step nobody can complete has to be said out loud. Otherwise the
            customer sits on a screen with a disabled button and no reason. */}
        {!loading && pkg && pkg.blocked.length > 0 && (
          <div className="rounded-lg border border-error-300 bg-error-100 px-3 py-2.5 text-sm text-neutral-900">
            {t(
              "legal_activation_blocked",
              "Some of the paperwork for this product is not published yet, so activation cannot be finished. Please contact us."
            )}
          </div>
        )}

        {!loading && pkg && pkg.maySign && current && (
          <div className="space-y-4">
            <StepProgress steps={steps} index={index} />

            {current.kind === "clickwrap" && (
              <ClickwrapStep
                key={current.code}
                step={current}
                simulate={usingSample}
                onRead={onRead}
                onDone={advance}
              />
            )}
            {current.kind === "third_party" && (
              <ThirdPartyStep key={current.code} step={current} simulate={usingSample} onDone={advance} />
            )}
            {current.kind === "consent" && (
              <ConsentStep
                key={current.code}
                step={current}
                screenVersion={pkg.consentScreenVersion}
                simulate={usingSample}
                onDone={advance}
              />
            )}
          </div>
        )}

        {!loading && pkg && !current && pkg.blocked.length === 0 && (
          <div className="space-y-4">
            <div className="flex items-start gap-2">
              <Check className="h-5 w-5 text-success-600 shrink-0 mt-0.5" />
              <p className="text-sm text-neutral-900">
                {t("legal_activation_done", "Everything needed for")} {productName}{" "}
                {t("legal_activation_done_tail", "is signed and in force.")}
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>{t("legal_close", "Close")}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ProductActivationDialog
